import { uimetadata } from '../metadata';
import { SupportedLanguage } from './types';

const translations = uimetadata;

/**
 * Translation utility class for handling internationalization
 */
export class TranslationUtils {
  private static instance: TranslationUtils;
  private currentLanguage: SupportedLanguage = 'en';
  private constructor() {
    // Initialize to default language; non-empty ctor satisfies lint
    this.currentLanguage = 'en';
  }

  public static getInstance(): TranslationUtils {
    if (!TranslationUtils.instance) {
      TranslationUtils.instance = new TranslationUtils();
    }
    return TranslationUtils.instance;
  }

  /**
   * Set the current language
   */
  public setLanguage(language: SupportedLanguage): void {
    this.currentLanguage = language;
  }

  /**
   * Get the current language
   */
  public getCurrentLanguage(): SupportedLanguage {
    return this.currentLanguage;
  }

  /**
   * Translate a key to the current language
   * @param key - Translation key in dot notation (e.g., 'common.loading')
   * @param fallback - Fallback text if translation not found
   * @returns Translated text
   */
  public translate(
    key: string,
    fallback?: string,
    params?: Record<string, string>
  ): string {
    const keys = key.split('.');
    let current: unknown = translations;

    for (const k of keys) {
      if (
        current &&
        typeof current === 'object' &&
        k in (current as Record<string, unknown>)
      ) {
        current = (current as Record<string, unknown>)[k];
      } else {
        // If translation not found, try fallback
        return this.interpolate(fallback || '', params);
      }
    }

    let result = '';
    // If current is a language object, return the value for currentLanguage
    if (
      current &&
      typeof current === 'object' &&
      this.currentLanguage in (current as Record<string, unknown>)
    ) {
      result =
        (current as Record<string, string>)[this.currentLanguage] ||
        fallback ||
        '';
    } else if (
      current &&
      typeof current === 'object' &&
      'en' in (current as Record<string, unknown>)
    ) {
      // If not found, try English
      result = (current as Record<string, string>)['en'] || fallback || '';
    } else {
      result = fallback || '';
    }

    return this.interpolate(result, params);
  }

  private interpolate(text: string, params?: Record<string, string>): string {
    if (!params) return text;
    return text.replace(/{{(\w+)}}/g, (_, key) => params[key] || `{{${key}}}`);
  }
  public getValue(key: string, fallback?: string): unknown {
    const keys = key.split('.');
    let current: unknown = translations;

    for (const k of keys) {
      if (
        current &&
        typeof current === 'object' &&
        k in (current as Record<string, unknown>)
      ) {
        current = (current as Record<string, unknown>)[k];
      } else {
        return '';
      }
    }

    return current || '';
  }

  /**
   * Check if a translation key exists
   */
  public hasTranslation(key: string, language?: SupportedLanguage): boolean {
    const lang = language || this.currentLanguage;
    const keys = key.split('.');
    let current: unknown = translations;

    for (const k of keys) {
      if (
        current &&
        typeof current === 'object' &&
        k in (current as Record<string, unknown>)
      ) {
        current = (current as Record<string, unknown>)[k];
      } else {
        return false;
      }
    }

    return !!(
      current &&
      typeof current === 'object' &&
      lang in (current as Record<string, unknown>) &&
      typeof (current as Record<string, unknown>)[lang] === 'string'
    );
  }

  /**
   * Get all available languages
   */
  public getAvailableLanguages(): SupportedLanguage[] {
    return Object.keys(translations) as SupportedLanguage[];
  }
}

// Export singleton instance
export const translationUtils = TranslationUtils.getInstance();
